import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useCompanyData } from '~/data/company';
import { fetchEmployeeLoginData } from '~/data/employee';
import { useEmployee } from '~/data/employee/useEmployee';
import { fetchEmployees } from '~/data/employees';
import { Roles } from '~/data/user';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import {
  CreateUserAccountSubmission,
  NewUserAccountDetails,
  createUserAccount,
} from '~/scenes/PeopleModule/components/EmployeeActions/CreateUserAccountDialog/data';
import { AppDispatch } from '~/store';

export enum UserAccountLoginWithMethod {
  Internal = 'internal',
  External = 'external',
}

type CreateUserAccountForm = {
  roles: Roles[];
  language: string;
  loginWith: UserAccountLoginWithMethod;
  username: string;
};

type PropsType = {
  employeeId: number;
  onClose: () => void;
};

export const useCreateUserAccountDialog = (props: PropsType) => {
  const { employeeId, onClose } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch: AppDispatch = useDispatch();

  const loggedInUserRoles = useLoggedInUserRoles();

  const { applicationConfig } = useApplicationConfig();
  const {
    isLoginWithSelectionEnabled,
    defaultLoginMethod,
    isExternalLoginOnly,
  } = useMemo(() => {
    const { fComExternalSignin, fComInternalSignin } =
      applicationConfig.fComConfig.coreConfig;

    const isExternalLoginOnly = fComExternalSignin && !fComInternalSignin;

    return {
      isLoginWithSelectionEnabled: fComExternalSignin && fComInternalSignin,
      isExternalLoginOnly,
      defaultLoginMethod: isExternalLoginOnly
        ? UserAccountLoginWithMethod.External
        : UserAccountLoginWithMethod.Internal,
    };
  }, [applicationConfig]);

  const { companyData } = useCompanyData();
  const { defaultCompanyLanguage, languageOptions } = useMemo(() => {
    return {
      defaultCompanyLanguage:
        companyData?.languages?.find(
          (language) => language.fComLanguageIsDefault,
        )?.fComLanguageCode ?? '',
      languageOptions: companyData?.languages ?? [],
    };
  }, [companyData]);

  const {
    employee: { empWorkContact, empBasicData },
    isLoading: isLoadingEmployee,
  } = useEmployee();

  const { hasWorkEmail, workEmail } = useMemo(() => {
    const hasWorkEmail =
      employeeId === empBasicData?.fEmpId &&
      empWorkContact?.fEmpEmailWork?.length > 0;

    return {
      hasWorkEmail,
      workEmail: hasWorkEmail ? empWorkContact?.fEmpEmailWork : '',
    };
  }, [empWorkContact, empBasicData, employeeId]);

  const [newAccountDetails, setNewAccountDetails] = useState<
    NewUserAccountDetails | undefined
  >(undefined);

  const initialForm: CreateUserAccountForm = {
    roles: [],
    language: defaultCompanyLanguage,
    loginWith: defaultLoginMethod,
    username: '',
  };

  const validationSchema = useMemo(() => {
    return yup
      .object()
      .shape<CreateUserAccountForm>({
        roles: yup
          .array()
          .of<Roles>(yup.mixed<Roles>().oneOf(Object.values(Roles)).required())
          .required(),
        language: yup.string().required(),
        loginWith: yup
          .mixed<UserAccountLoginWithMethod>()
          .oneOf(Object.values(UserAccountLoginWithMethod)),
        username: yup.string(),
      })
      .test('rolesSize', t('fieldIsRequired'), (value, context) => {
        // Hacky workaround to test if array is empty
        const path = !!context.path ? `${context.path}.roles` : 'roles';

        return value?.roles.length === 0
          ? context.createError({
              message: t('fieldIsRequired'),
              path: path,
            })
          : true;
      });
  }, [t]);

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const form = useForm<CreateUserAccountForm>({
    resolver,
    defaultValues: initialForm,
  });

  const { setValue, reset, handleSubmit } = form;

  const updateUserNameByLoginMethod = useCallback(
    (loginMethod: string) => {
      switch (loginMethod) {
        case UserAccountLoginWithMethod.External:
          setValue('username', workEmail);
          break;
        case UserAccountLoginWithMethod.Internal:
          setValue('username', '');
          break;
      }
    },
    [setValue, workEmail],
  );

  const onCancel = useCallback(() => {
    reset();
    setNewAccountDetails(undefined);
    onClose();
  }, [onClose, reset, setNewAccountDetails]);

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const { fEmpId, fEmpFirstName, fEmpLastName } = empBasicData;
      const { language, username, roles, loginWith } = data;

      const payload: CreateUserAccountSubmission = {
        fLoginEmpId: fEmpId,
        fLoginUserFirstname: fEmpFirstName,
        fLoginUserLastname: fEmpLastName,
        fLoginUserLanguage: language,
        fLoginUsername: username,
        fLoginUserRoles: roles,
        fLoginUserIsExternal: loginWith === 'external',
      };

      return createUserAccount(payload)
        .then((data) => {
          setNewAccountDetails(data);
        })
        .catch(() => {
          enqueueSnackbar(t('laError'), {
            variant: 'error',
          });
        });
    });
  }, [handleSubmit, empBasicData, enqueueSnackbar, t, setNewAccountDetails]);

  const onComplete = useCallback(() => {
    dispatch(fetchEmployeeLoginData({ empId: employeeId }));
    dispatch(fetchEmployees({ myRoles: loggedInUserRoles }));
    onCancel();
  }, [onCancel, dispatch, employeeId, loggedInUserRoles]);

  return {
    onCancel,
    onSubmit,
    onComplete,
    isLoading: isLoadingEmployee,
    hasWorkEmail,
    isLoginWithSelectionEnabled,
    form,
    languageOptions,
    updateUserNameByLoginMethod,
    empBasicData,
    newAccountDetails,
    showNewPassword: !(
      isExternalLoginOnly || !!newAccountDetails?.fLoginIsExternal
    ),
  };
};
